import React, { useState } from 'react';
import { ArrowLeft, Save, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminService, { CreateTestimonialRequest, TestimonialStatus } from '../../services/admin/adminService';

const CreateTestimonial: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateTestimonialRequest>({
    name: '',
    text: '',
    rating: 5,
    location: '',
    profile: '',
    category: '',
    status: 'pending',
    verified: false,
    featured: false,
    helpful: 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'rating') {
      setFormData(prev => ({
        ...prev,
        [name]: parseInt(value) || 5
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formData.text.trim()) {
      toast.error('Testimonial text is required');
      return;
    }

    try {
      setLoading(true);
      const dataToSend: CreateTestimonialRequest = {
        ...formData,
        location: formData.location || null,
        profile: formData.profile || null,
        category: formData.category || null,
      };
      
      await adminService.createTestimonial(dataToSend);
      toast.success('Testimonial created successfully!');
      navigate('/admin/testimonials');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create testimonial');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
            className="focus:outline-none"
          >
            <Star
              size={32}
              className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
            />
          </button>
        ))}
        <span className="ml-2 text-sm text-secondary-600">({rating}/5)</span>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/testimonials')}
          className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={24} className="text-secondary-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Create Testimonial</h1>
          <p className="text-secondary-600 mt-1">Add a new customer testimonial</p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-secondary-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter customer name"
            />
          </div>

          {/* Testimonial Text */}
          <div>
            <label htmlFor="text" className="block text-sm font-medium text-secondary-700 mb-2">
              Testimonial <span className="text-red-500">*</span>
            </label>
            <textarea
              id="text"
              name="text"
              value={formData.text}
              onChange={handleChange}
              required
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter testimonial text"
            />
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Rating <span className="text-red-500">*</span>
            </label>
            {renderStars(formData.rating || 5)}
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-secondary-700 mb-2">
              Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter location (optional)"
            />
          </div>

          {/* Profile */}
          <div>
            <label htmlFor="profile" className="block text-sm font-medium text-secondary-700 mb-2">
              Profile Image URL
            </label>
            <input
              type="text"
              id="profile"
              name="profile"
              value={formData.profile || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter profile image URL (optional)"
            />
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-secondary-700 mb-2">
              Category
            </label>
            <input
              type="text"
              id="category"
              name="category"
              value={formData.category || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter category (optional)"
            />
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-secondary-700 mb-2">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Verified & Featured */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="verified"
                name="verified"
                checked={formData.verified}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="verified" className="ml-2 text-sm font-medium text-secondary-700">
                Verified
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="featured" className="ml-2 text-sm font-medium text-secondary-700">
                Featured
              </label>
            </div>
          </div>

          {/* Helpful */}
          <div>
            <label htmlFor="helpful" className="block text-sm font-medium text-secondary-700 mb-2">
              Helpful Count
            </label>
            <input
              type="number"
              id="helpful"
              name="helpful"
              value={formData.helpful || 0}
              onChange={(e) => setFormData(prev => ({ ...prev, helpful: parseInt(e.target.value) || 0 }))}
              min="0"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter helpful count"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/admin/testimonials')}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Save size={18} />
              {loading ? 'Creating...' : 'Create Testimonial'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTestimonial;
